// web/src/components/ceremony/UnsealCeremony.tsx — the M3 unseal-ceremony container
// mounted at /unseal/:id (the centerpiece; backs the UNSEAL dynamic C4).
//
// It owns the ceremony state machine (sealed -> collecting -> unlocked) and the
// aria-live announcements (motion-spec §5); the VaultSafe is pure presentational.
// Three role-gated views (wireframe 06):
//   • requester  — gathers shards; at K recombines + decrypts CLIENT-SIDE.
//   • holder     — sees the consequence + identity, decrypts THEIR OWN shard
//                  locally and delivers it e2e (ShardSubmitPanel).
//   • observer   — read-only progress.
//
// Realtime drives the animation: a ceremony-scoped WebSocket fans `shard.submitted`
// (each retracts a bolt + steps the dial) and `request.unlocked`. The authoritative
// tally is `submittedCount` from those events / GET /quorum/:id/status — the safe
// re-renders to the correct bolt position on resync, never replaying (Principle 5).
//
// Reconstruction is CLIENT-SIDE ONLY (R11): the requester collects K DECRYPTED
// shares over the e2e transport (transport.ts — never the server) and calls
// crypto.combineAndOpen locally; the server only ever sees the ciphertext-free
// `shard.submitted` tally. The door opens and the reveal modal shows the secret.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ShieldCheck, Eye } from 'lucide-react';
import type { Principal, RealtimeEvent, UnsealStatus } from '../../../../shared/types';
import { api } from '../../api';
import { RealtimeClient } from '../../ws';
import {
  combineAndOpen,
  decryptShare,
  keyManager,
  type Envelope,
  type PublicKeyId,
  type PublicKeyJwk,
  type RawShare,
  type WireShard,
} from '../../crypto';
import { Icon } from '../ui/Icon';
import { EmptyState, FeedbackMessage } from '../ui';
import { ShieldBanner } from '../enroll/ShieldBanner';
import { VaultSafe, type SafeState } from './VaultSafe';
import { QuorumRoster, type RosterHolder, type HolderState } from './QuorumRoster';
import { ShardSubmitPanel } from './ShardSubmitPanel';
import { RevealModal } from './RevealModal';
import {
  createShareTransport,
  type RequesterEphemeralKey,
  type ShareEnvelope,
} from './transport';
import { parseEnvelope } from './envelope';

export type CeremonyRole = 'requester' | 'holder' | 'observer';

/** One eligible holder in the ceremony (from the sealed record's member set). */
export interface CeremonyHolder {
  memberId: string;
  label: string;
  fingerprint?: string;
}

/**
 * The ceremony context. The requester/convene path hands it in via router state
 * when /unseal/:id is opened from a record. The HOLDER path (Requests inbox) lands
 * with no state — the container then self-hydrates a holder context from GET
 * /quorum/:id/status (the secretId) + GET /records/:id/my-shard (the holder's own
 * opaque shard). Failing both, the page renders the read-only observer view from
 * the GET /quorum/:id/status tally alone.
 */
export interface CeremonyContext {
  recordName: string;
  thresholdK: number;
  totalN: number;
  requesterLabel: string;
  requesterFingerprint?: string;
  holders: CeremonyHolder[];
  /** The viewer's role in THIS ceremony. */
  role: CeremonyRole;
  /** Holder-only: this viewer's own member slot + the data needed to decrypt it. */
  ownMember?: {
    memberId: string;
    shareIndex: number;
    keyId: PublicKeyId; // the holder's local (non-extractable) decryption key
    shard: WireShard; // the holder's OWN ciphertext shard (decrypted locally)
  };
  /** Requester-only: the public envelope to open once K shares are recombined. */
  envelope?: Envelope;
  /**
   * Requester-only: the ephemeral ECDH keypair generated at convene time (M3.2).
   * Holders re-encrypt their decrypted share to its PUBLIC key; this in-memory
   * PRIVATE handle decrypts the relayed blobs as they arrive cross-device. Carried
   * via router state (structured-clone preserves the non-extractable CryptoKey).
   */
  requesterEphemeral?: RequesterEphemeralKey;
}

interface StatusView {
  status: UnsealStatus;
  secretId: string;
  submittedCount: number;
  thresholdK: number;
  expiresAt: number;
  /** The requester's ephemeral PUBLIC key (JSON); a holder seals its share TO it (M3.2). */
  requesterEphemeralJwk: string | null;
}

function safeStateFor(status: UnsealStatus, submitted: number, k: number): SafeState {
  if (status === 'unlocked') return 'unlocked';
  if (submitted <= 0) return 'sealed';
  if (submitted >= k) return 'unlocked';
  return 'collecting';
}

export function UnsealCeremony({
  principal,
}: {
  principal: Principal;
}): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navCtx = (location.state as { ceremony?: CeremonyContext } | null)?.ceremony ?? null;

  // The ceremony context. The requester/convene path hands it in via router state;
  // the holder path (Requests inbox -> /unseal/:id) lands with no state, so we
  // SELF-HYDRATE a holder context below from GET /quorum/:id/status (the secretId)
  // + GET /records/:id/my-shard (the holder's own opaque shard, decrypted locally).
  const [hydrated, setHydrated] = useState<CeremonyContext | null>(null);
  const ctx: CeremonyContext | null = navCtx ?? hydrated;

  // Role: explicit from context, else observer (read-only) by default.
  const role: CeremonyRole = ctx?.role ?? 'observer';
  const thresholdK = ctx?.thresholdK ?? 0;
  const totalN = ctx?.totalN ?? (ctx?.holders.length ?? 0);

  const [view, setView] = useState<StatusView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const [assertive, setAssertive] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [declined, setDeclined] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Set when the requester has quorum but the envelope ciphertext is unavailable
  // (a known backend gap — the encrypted-secret blob has no delivery path yet).
  const [envelopeMissing, setEnvelopeMissing] = useState(false);

  // Member states the requester observes (which holders have submitted).
  const [holderStates, setHolderStates] = useState<Record<string, HolderState>>({});

  // The requester collects K DECRYPTED shares e2e (client-side only) to recombine.
  const sharesRef = useRef<Map<number, RawShare>>(new Map());
  const recombinedRef = useRef(false); // door fires once (motion-spec §5)
  // A lazily-resolved envelope (the encrypted-secret blob) when the convene context
  // didn't carry one. Cached so we attempt the read-path at most once at quorum.
  const envelopeRef = useRef<Envelope | null>(ctx?.envelope ?? null);

  // One-shot hydrate of the authoritative tally (safe renders in current state,
  // no spinner — wireframe 06 Loading).
  useEffect(() => {
    let cancelled = false;
    api
      .unsealStatus(id)
      .then((s) => {
        if (cancelled) return;
        setView({
          status: s.status,
          secretId: s.secretId,
          submittedCount: s.submittedCount,
          thresholdK: s.thresholdK,
          expiresAt: s.expiresAt,
          requesterEphemeralJwk: s.requesterEphemeralJwk,
        });
      })
      .catch((e) => !cancelled && setError((e as Error)?.message ?? 'Could not load the ceremony.'));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Holder self-hydration (the Requests-inbox path). When the page is opened
  // without a requester-supplied context, try to stand up the HOLDER view from
  // the caller's own opaque shard: GET /records/:secretId/my-shard returns the
  // ciphertext sealed to THIS device's public key (403 to a non-holder). We parse
  // it into a WireShard, take the recipient key id off the shard itself (no guess),
  // confirm the matching local private key exists, and build the roster from the
  // record's live members. Everything stays metadata-only until the holder consents
  // in ShardSubmitPanel, where the shard is decrypted on-device (R11).
  const secretId = view?.secretId ?? '';
  useEffect(() => {
    // Only self-hydrate when we have no explicit context and we know the record.
    if (navCtx || hydrated || !secretId) return;
    let cancelled = false;
    void (async () => {
      try {
        const mine = await api.myShard(secretId);
        if (cancelled || !mine.shardCiphertext) return;
        const shard = JSON.parse(mine.shardCiphertext) as WireShard;
        const keyId = shard.recipientKeyId;
        // The holder must actually hold the matching private key on this device.
        await keyManager.getPrivateKey(keyId);
        if (cancelled) return;
        let recordName = 'Sealed record';
        let holders: CeremonyHolder[] = [];
        let recordK = thresholdK;
        let recordN = totalN;
        try {
          const detail = await api.getRecord(secretId);
          recordName = detail.record.description || recordName;
          recordK = detail.record.thresholdK;
          recordN = detail.record.totalN;
          holders = detail.members
            .filter((m) => !m.shardRevoked)
            .map((m) => ({
              memberId: m.memberId,
              label: m.email,
              fingerprint: m.fingerprint ?? undefined,
            }));
        } catch {
          /* detail is best-effort; the submit path needs only the shard + key */
        }
        if (cancelled) return;
        setHydrated({
          recordName,
          thresholdK: recordK,
          totalN: recordN,
          requesterLabel: 'the requester',
          holders,
          role: 'holder',
          ownMember: {
            memberId: mine.memberId,
            shareIndex: mine.shareIndex,
            keyId,
            shard,
          },
        });
      } catch {
        // No shard for this caller (a non-holder / observer landing) or the local
        // private key is missing — leave the read-only observer view in place.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navCtx, hydrated, secretId, thresholdK, totalN]);

  // The requester-side recombine: at K decrypted shares, combineAndOpen locally.
  const tryRecombine = useCallback(async () => {
    if (recombinedRef.current) return;
    if (role !== 'requester') return;
    const shares = [...sharesRef.current.values()];
    if (shares.length < thresholdK) return;

    // combineAndOpen needs the envelope CIPHERTEXT — the AES-GCM-sealed secret
    // blob. Prefer the convene-supplied envelope; otherwise make one real attempt
    // to resolve it from the record read-path (forward-compatible: lights up with
    // no web change the moment the backend delivers the ciphertext).
    let envelope = envelopeRef.current;
    if (!envelope && secretId) {
      try {
        const detail = await api.getRecord(secretId);
        envelope = parseEnvelope(detail);
        envelopeRef.current = envelope;
      } catch {
        /* read-path blip; treated as the gap below */
      }
    }

    // KNOWN BACKEND GAP (reported to the orchestrator; see ceremony/envelope.ts):
    // the seal write-path drops the envelope ciphertext (the API allow-lists only
    // v/aeadAlg/sssScheme/iv in envelopeMeta and rejects ciphertext), and no read
    // endpoint returns it. We have K shares and can rebuild the data key, but with
    // no ciphertext there is nothing to decrypt. We surface this honestly rather
    // than fabricate a reveal (R11 / brief #2).
    if (!envelope) {
      recombinedRef.current = true;
      setEnvelopeMissing(true);
      setAssertive(
        'Quorum reached. The encrypted secret blob is not yet retrievable to this device — see the notice below.',
      );
      return;
    }
    recombinedRef.current = true;
    try {
      const pt = await combineAndOpen(shares.slice(0, thresholdK), envelope);
      const text = new TextDecoder().decode(pt);
      pt.fill(0); // wipe the plaintext bytes; the string lives only in this view
      setRevealed(text);
      setAssertive(
        'Quorum reached — vault unlocked. Plaintext is now in your browser.',
      );
    } catch (e) {
      recombinedRef.current = false;
      setError(
        (e as Error)?.message ??
          'A submitted shard failed integrity — the ceremony is not compromised but cannot complete with it.',
      );
    }
  }, [role, thresholdK, secretId]);

  // E2E share transport (requester side): collect decrypted shares as they arrive.
  // PRIMARY cross-device path is the encrypted store-and-forward relay — the
  // transport drains /deliveries and decrypts each opaque blob with the requester's
  // EPHEMERAL private handle (held in ctx, never sent anywhere). The BroadcastChannel
  // fallback still covers same-browser multi-tab. The server never sees a share.
  const requesterEphemeral = ctx?.requesterEphemeral;
  useEffect(() => {
    if (role !== 'requester') return;
    const transport = createShareTransport(id, { ephemeral: requesterEphemeral });
    const off = transport.onShare((env: ShareEnvelope) => {
      if (env.requestId !== id) return;
      sharesRef.current.set(env.shareIndex, env.share);
      void tryRecombine();
    });
    return () => {
      off();
      transport.close();
    };
  }, [id, role, requesterEphemeral, tryRecombine]);

  // Ceremony-scoped realtime: filter to THIS request's tally events. Each
  // shard.submitted advances the safe + announces; request.unlocked flips state.
  useEffect(() => {
    const client = new RealtimeClient();
    const off = client.on((ev: RealtimeEvent) => {
      switch (ev.type) {
        case 'shard.submitted': {
          if (ev.submission.requestId !== id) return;
          const { submittedCount, thresholdK: k } = ev.submission;
          setView((cur) =>
            cur
              ? { ...cur, submittedCount: Math.max(cur.submittedCount, submittedCount) }
              : {
                  status: 'gathering',
                  secretId: '',
                  submittedCount,
                  thresholdK: k,
                  expiresAt: 0,
                  requesterEphemeralJwk: null,
                },
          );
          setHolderStates((cur) => ({ ...cur, [ev.submission.memberId]: 'submitted' }));
          setAnnounce(`${submittedCount} of ${k} shards submitted`);
          break;
        }
        case 'request.unlocked': {
          if (ev.request.id !== id) return;
          setView((cur) =>
            cur
              ? { ...cur, status: 'unlocked', submittedCount: ev.request.submittedCount }
              : {
                  status: 'unlocked',
                  secretId: ev.request.secretId,
                  submittedCount: ev.request.submittedCount,
                  thresholdK: ev.request.thresholdK,
                  expiresAt: ev.request.expiresAt,
                  requesterEphemeralJwk: ev.request.requesterEphemeralJwk,
                },
          );
          // Requester: recombine now if the shares are already collected.
          void tryRecombine();
          break;
        }
      }
    });
    client.connect();
    return () => {
      off();
      client.close();
    };
  }, [id, tryRecombine]);

  // Holder action: decrypt OWN shard locally (R11), deliver e2e, record the tally.
  const submitShard = useCallback(async () => {
    if (!ctx?.ownMember) throw new Error('No shard slot for this device.');
    const { memberId, shareIndex, keyId, shard } = ctx.ownMember;
    // 8–9 of the dynamic: load the non-extractable private key + local decrypt.
    const privateKey = await keyManager.getPrivateKey(keyId);
    const share = await decryptShare(shard, privateKey);

    // 10: deliver the decrypted share e2e to the requester (never readable server-
    // side). The transport RE-ENCRYPTS the raw share to the requester's EPHEMERAL
    // public key (fetched on /status) before it leaves this device, then POSTs the
    // opaque blob to the relay — fixing cross-device delivery (M3.2). If the
    // ephemeral key is absent (legacy ceremony) it degrades to same-browser only.
    let deliver: { memberId: string; recipientKeyId: PublicKeyId; recipientPublicKey: PublicKeyJwk } | undefined;
    const ephemeralJwk = view?.requesterEphemeralJwk;
    if (ephemeralJwk) {
      const recipientPublicKey = JSON.parse(ephemeralJwk) as PublicKeyJwk;
      const recipientKeyId = await keyManager.deriveKeyId(recipientPublicKey);
      deliver = { memberId, recipientKeyId, recipientPublicKey };
    }
    const transport = createShareTransport(id, { deliver });
    await transport.send({ requestId: id, shareIndex, share });
    transport.close();

    // 11–13: record the ciphertext-free tally (drives everyone's safe).
    await api.submitShard(id, { memberId });
    setSubmitted(true);
  }, [ctx?.ownMember, id, view?.requesterEphemeralJwk]);

  const status: UnsealStatus = view?.status ?? 'gathering';
  const submittedCount = view?.submittedCount ?? 0;
  const k = view?.thresholdK || thresholdK || 1;
  const safeState = safeStateFor(status, submittedCount, k);

  // Build the roster rows (requester/observer see live per-holder state).
  const rosterHolders: RosterHolder[] = useMemo(
    () =>
      (ctx?.holders ?? []).map((h) => ({
        memberId: h.memberId,
        label: h.label,
        meta: h.fingerprint,
        state: holderStates[h.memberId] ?? 'pending',
      })),
    [ctx?.holders, holderStates],
  );

  if (error) {
    return (
      <div className="bgUnseal">
        <FeedbackMessage ok={false} msg={error} />
      </div>
    );
  }

  const recordName = ctx?.recordName ?? 'Sealed record';

  return (
    <div className="bgUnseal">
      <div className="bgUnsealHead">
        <h1>{recordName}</h1>
        <span className="bgUnsealRole">
          {role === 'requester'
            ? 'you are the requester'
            : role === 'holder'
              ? 'you hold an eligible shard'
              : 'you are watching this ceremony'}
        </span>
      </div>

      <ShieldBanner
        lead="Each holder decrypts their own shard on their device; the server only relays a ciphertext-free tally."
        note="Reconstruction happens in the requester's browser at quorum — nothing reconstructs server-side."
      />

      {/* aria-live regions (motion-spec §6). Polite for per-shard progress;
          assertive for the unlock moment. Always present for parity. */}
      <div aria-live="polite" className="bgSafeMeterVisual">
        {announce}
      </div>
      <div aria-live="assertive" className="bgSafeMeterVisual">
        {assertive}
      </div>

      <div className="bgUnsealGrid">
        <div className="bgSafeStage">
          <VaultSafe submitted={submittedCount} threshold={k} state={safeState} />
          <div className="bgCeremonyChips">
            {safeState === 'collecting' && (
              <span className="bgLiveBadge">
                <span className="bgLiveDot" aria-hidden="true" /> Live
              </span>
            )}
            {role === 'observer' && (
              <span className="bgLiveBadge" style={{ color: 'var(--color-text2)' }}>
                <Icon icon={Eye} size="xs" aria-hidden="true" /> Read-only
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          <QuorumRoster holders={rosterHolders} thresholdK={k} totalN={totalN} />

          {role === 'holder' && !submitted && !declined && status === 'gathering' && (
            <ShardSubmitPanel
              recordName={recordName}
              requesterLabel={ctx?.requesterLabel ?? principal.email ?? 'the requester'}
              requesterFingerprint={ctx?.requesterFingerprint}
              thresholdK={k}
              totalN={totalN}
              onSubmit={submitShard}
              onDecline={() => setDeclined(true)}
            />
          )}
          {role === 'holder' && submitted && (
            <FeedbackMessage ok msg="Your shard was decrypted on this device and sent to the requester." />
          )}
          {role === 'holder' && declined && (
            <FeedbackMessage ok={false} msg="You declined. The requester sees you as outstanding." />
          )}

          {role === 'requester' && status === 'gathering' && !envelopeMissing && (
            <div className="bgQuorumFooter">
              <Icon icon={ShieldCheck} size="xs" aria-hidden="true" /> Reconstruction
              happens in YOUR browser once {k} shards arrive. Nothing reconstructs
              server-side.
            </div>
          )}

          {role === 'requester' && envelopeMissing && (
            <FeedbackMessage
              ok={false}
              msg="Quorum reached and the data key was rebuilt in your browser — but the encrypted secret blob isn't retrievable to this device yet, so it can't be opened. This is a known gap pending a server change to deliver the envelope ciphertext. No reveal is faked."
            />
          )}

          {(ctx?.holders.length ?? 0) === 0 && role !== 'holder' && (
            <EmptyState
              title="Waiting for the quorum"
              subtitle="No holders have joined yet — they have been notified."
            />
          )}
        </div>
      </div>

      {revealed !== null && (
        <RevealModal
          recordName={recordName}
          plaintext={revealed}
          expiresAt={view?.expiresAt ?? Date.now()}
          onClose={() => setRevealed(null)}
        />
      )}
    </div>
  );
}

export default UnsealCeremony;
